// SlopeLayer - Visualizes slope angle (steepness) from DEM tiles
// Inherits common tile management logic from TerrainLayer
// Specific to slope: DEM decoding, Sobel gradient computation

import { gaussianBlur, normalizeSlope } from '../../utils/terrainCalculations';
import TerrainLayer from './TerrainLayer';

class SlopeLayer extends TerrainLayer {
    /**
     * Get path to slope worker script
     */
    getWorkerPath () {
        return '/static/workers/slopeWorker.js';
    }

    /**
     * Initialize slope layer
     * @param {string} accessToken - Mapbox access token
     * @param {mapboxgl.Map} map - Mapbox map instance
     */
    constructor (accessToken, map) {
        super(accessToken, map, { layerNamePrefix: 'dem' });
    }

    /**
     * Post message to worker with zoom parameter
     */
    postMessageToWorker (worker, imageData, z, x, y) {
        worker.postMessage({
            imageData,
            zoom: z,
            x,
            y,
        });
    }

    /**
     * Add slope layer to map with terrain optimization
     */
    addToMap () {
        if (this.isActive) {
            return;
        }
        // Call parent addToMap first
        super.addToMap();
        // Then optimize for terrain if needed
        this.optimizeForTerrain();
    }

    /**
     * Optimize worker pool based on whether 3D terrain is active
     */
    optimizeForTerrain () {
        // Check if Mapbox 3D terrain is enabled
        const terrain = this.map.getTerrain();
        const has3DTerrain = terrain !== null && typeof terrain !== 'undefined';
        // If 3D terrain is active, add more workers (max 8 instead of 4)
        if (has3DTerrain && this.workers.length < 8) {
            const additionalWorkers = Math.min(4, 8 - this.workers.length);
            for (let i = 0; i < additionalWorkers; i++) {
                const worker = new Worker('/static/workers/slopeWorker.js');
                worker.isAvailable = true;
                this.workers.push(worker);
            }
        }
    }

    /**
     * Remove slope layer from map
     */
    removeFromMap () {
        if (!this.isActive) {
            return;
        }
        // Call parent removeFromMap
        super.removeFromMap();
    }

    /**
     * Custom render handler for slope layer with zoom and memory management
     */
    onMapRender () {
        if (!this.isActive) {
            return;
        }
        const currentZoom = this.map.getZoom();
        const minZoom = 7;
        const maxZoom = 17;

        // Hide layer if zoom is below minimum
        if (currentZoom < minZoom) {
            return;
        }

        // Clamp zoom to maxZoom for tile loading (18+ will use z=17 tiles)
        const zoom = Math.min(Math.floor(currentZoom), maxZoom);

        // Detect zoom change
        if (zoom !== this.currentZoom) {
            this.lastZoomChangeTime = Date.now();
        }
        this.currentZoom = zoom;

        // Mark this zoom level as having pending loads
        this.tilesLoadingFromZoom.add(zoom);

        const bounds = this.map.getBounds();
        const visibleTiles = this.getVisibleTiles(bounds, zoom);

        // Load tiles with concurrent limit to avoid memory/CPU overload
        for (const { z, x, y } of visibleTiles) {
            if (this.pendingTiles.size >= this.maxConcurrentTiles) {
                break; // Stop loading if we hit the concurrent limit
            }
            this.loadAndAddTile(z, x, y);
        }

        // Update visibility of tiles based on current viewport
        // Note: Tiles remain in cache, only map visibility is toggled
        // Cleanup old tiles if we have too many in memory
        this.performMemoryCleanup(visibleTiles, zoom);
    }

    /**
     * Periodic cleanup of old tiles when memory pressure is high
     */
    performMemoryCleanup (visibleTiles, zoom) {
        // Only do cleanup if we have accumulated too many layers (>100)
        if (this.activeLayers.size > 100) {
            this.hardClearOldTiles(visibleTiles);
        }
    }

    /**
     * Hard cleanup of old tiles - only when memory pressure is high
     */
    hardClearOldTiles (visibleTiles) {
        const visibleKeys = new Set(visibleTiles.map(t => `${t.z}-${t.x}-${t.y}`));
        const toRemove = [];

        for (const key of this.activeLayers) {
            if (!visibleKeys.has(key)) {
                toRemove.push(key);
            }
        }

        // Remove half of the off-screen tiles
        const removeCount = Math.floor(toRemove.length / 2);
        for (let i = 0; i < removeCount; i++) {
            const key = toRemove[i];
            const layerId = `dem-layer-${key}`;
            const sourceId = `dem-source-${key}`;
            if (this.map.getLayer(layerId)) {
                this.map.removeLayer(layerId);
            }
            if (this.map.getSource(sourceId)) {
                this.map.removeSource(sourceId);
            }
            this.activeLayers.delete(key);
        }
    }

    /**
     * Load and add tile to map - override to use terrain mode
     */
    async loadAndAddTile (z, x, y) {
        const key = `${z}-${x}-${y}`;
        if (this.activeLayers.has(key)) {
            return;
        }
        if (this.pendingTiles.has(key)) {
            return;
        }
        this.pendingTiles.add(key);

        const controller = new AbortController();
        this.pendingRequests.set(key, controller);

        try {
            let tileCanvas = this.tileCache.get(key);
            if (!tileCanvas) {
                tileCanvas = await this.fetchTileWithFallback(z, x, y, controller.signal);
                if (!tileCanvas) {
                    throw new Error('No tile available even with fallback');
                }
                this.tileCache.set(key, tileCanvas);
            }

            if (!this.isActive) {
                this.pendingRequests.delete(key);
                this.pendingTiles.delete(key);
                return;
            }

            const dataUrl = tileCanvas.toDataURL('image/png');
            const { nwLng, nwLat, neLng, neLat, seLng, seLat, swLng, swLat } = this.getTileCoordinates(z, x, y);
            const sourceId = `dem-source-${key}`;
            if (!this.map.getSource(sourceId)) {
                this.map.addSource(sourceId, {
                    type: 'image',
                    url: dataUrl,
                    coordinates: [ [ nwLng, nwLat ], [ neLng, neLat ], [ seLng, seLat ], [ swLng, swLat ] ],
                });
            }
            const layerId = `dem-layer-${key}`;
            if (!this.map.getLayer(layerId)) {
                this.map.addLayer({
                    id: layerId,
                    type: 'raster',
                    source: sourceId,
                    paint: {
                        'raster-opacity': 1.0,
                    },
                }, 'building');
                this.activeLayers.add(key);
            }
        } catch (error) {
            // Silently fail
        } finally {
            this.pendingTiles.delete(key);
            this.pendingRequests.delete(key);

            const hasPendingForZoom = Array.from(this.pendingTiles).some(
                tileKey => tileKey.startsWith(`${z}-`)
            );
            if (!hasPendingForZoom) {
                this.tilesLoadingFromZoom.delete(z);
            }
        }
    }

    /**
     * Process tile blob - decode DEM and calculate slope
     * Uses worker for performance, falls back to main thread if worker fails
     */
    async processTile (blob, z, x, y) {
        const blobUrl = URL.createObjectURL(blob);
        try {
            const img = await this.loadImage(blobUrl);
            return await this.processImage(img, z, x, y);
        } finally {
            URL.revokeObjectURL(blobUrl);
        }
    }

    /**
     * Fallback: Process image on main thread (if worker fails)
     */
    processImageMainThread = (img, z, x, y) => {
        const tileSize = 512;
        const canvas = document.createElement('canvas');
        canvas.width = tileSize;
        canvas.height = tileSize;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, tileSize, tileSize);
        const data = imageData.data;
        let heights = new Float32Array(tileSize * tileSize);
        for (let i = 0; i < data.length; i += 4) {
            const pixelIdx = i / 4;
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            heights[pixelIdx] = (r * 256 + g + b / 256) - 32768;
        }

        // Apply Gaussian blur at high zoom levels to reduce noise
        if (z > 15) {
            heights = gaussianBlur(heights, tileSize, tileSize);
        }

        for (let y = 0; y < tileSize; y++) {
            for (let x = 0; x < tileSize; x++) {
                const idx = y * tileSize + x;
                const height = heights[idx];

                if (height <= 0) {
                    const pixelIdx = idx * 4;
                    data[pixelIdx] = 0;
                    data[pixelIdx + 1] = 0;
                    data[pixelIdx + 2] = 0;
                    data[pixelIdx + 3] = 0;
                    continue;
                }

                const sx = x === 0 ? 1 : (x === tileSize - 1 ? tileSize - 2 : x);
                const sy = y === 0 ? 1 : (y === tileSize - 1 ? tileSize - 2 : y);
                let slope = this.calculateSlope(heights, sx, sy, tileSize, z);
                // Apply zoom-adaptive normalization
                slope = normalizeSlope(slope, z);
                const color = this.slopeToColor(slope);
                const pixelIdx = idx * 4;
                data[pixelIdx] = color.r;
                data[pixelIdx + 1] = color.g;
                data[pixelIdx + 2] = color.b;
                data[pixelIdx + 3] = 255;
            }
        }

        ctx.putImageData(imageData, 0, 0);
        return canvas;
    };

    /**
     * Calculate slope in degrees for a pixel using Sobel operator
     * NOTE: This is LEGACY FALLBACK CODE for when worker processing fails.
     * Primary processing happens via slopeWorker.js which uses shared terrainCalculations.js.
     * This fallback runs on main thread only if worker crashes, so performance is less critical.
     */
    calculateSlope = (heights, x, y, tileSize, z) => {
        // Sobel operator for gradient (in height units)
        const gx = (
            -1 * heights[(y - 1) * tileSize + (x - 1)] +
            1 * heights[(y - 1) * tileSize + (x + 1)] +
            -2 * heights[y * tileSize + (x - 1)] +
            2 * heights[y * tileSize + (x + 1)] +
            -1 * heights[(y + 1) * tileSize + (x - 1)] +
            1 * heights[(y + 1) * tileSize + (x + 1)]
        ) / 8;
        const gy = (
            -1 * heights[(y - 1) * tileSize + (x - 1)] +
            1 * heights[(y + 1) * tileSize + (x - 1)] +
            -2 * heights[(y - 1) * tileSize + x] +
            2 * heights[(y + 1) * tileSize + x] +
            -1 * heights[(y - 1) * tileSize + (x + 1)] +
            1 * heights[(y + 1) * tileSize + (x + 1)]
        ) / 8;
        // Calculate pixel size in meters based on zoom level
        // Earth circumference ≈ 40075km, each tile = 512 pixels
        // At zoom z: number of tiles = 2^z
        // Tile size = 40075000m / 2^z
        // Pixel size = (40075000 / 2^z) / 512
        const earthCircumference = 40075000;
        const tilesAtZoom = Math.pow(2, z);
        const tileMeters = earthCircumference / tilesAtZoom;
        const pixelMeters = tileMeters / tileSize;
        const rise = Math.sqrt(gx * gx + gy * gy);
        const run = 2 * pixelMeters;
        const slopeDegrees = Math.atan(rise / run) * 180 / Math.PI;
        return slopeDegrees;
    };
}

export default SlopeLayer;
